import { useEffect, useState } from 'react';
import { useDelayCall } from '@equifinance/shared';
import _ from 'lodash';
import { useAccountContext } from '../account';
import useProductsApi, { ProductsType } from '../../api/useProductsApi';
import { ProductLoanDetails } from './Products';
import { defaultProductLoanDetails } from './defaultProductLoanDetails';
import { useProductsContext } from './ProductsProvider';

export function useProducts(id: string) {
  const [productsObject, setProductsObject] = useState<ProductsType>();
  const [productLoanDetails, setProductLoanDetails] = useState<ProductLoanDetails>(defaultProductLoanDetails);
  const {
    productsResultDigest,
    creditSearchCompleted,
    setProductsResultDigest,
  } = useAccountContext();
  const { getProducts, getProductLoanDetails } = useProductsApi();

  const incrementedUpdateCount = (prevUpdateCount: number) => (prevUpdateCount ? prevUpdateCount + 1 : 1);

  const updateProductLoanDetails = async (field: string, value: any, disableIncrementUpdateCount: boolean = false) => {
    setProductLoanDetails((prev) => {
      const clone = _.cloneDeep(prev);
      const newAccount = _.set(clone, field, value);
      return {
        ...newAccount,
        updateCount: (disableIncrementUpdateCount ? prev?.updateCount : incrementedUpdateCount(prev?.updateCount)),
        dirtyCount: incrementedUpdateCount(prev?.dirtyCount),
      };
    });
  };

  const fetchProductLoanDetails = async () => {
    if (!creditSearchCompleted) return;
    const result = await getProductLoanDetails(id);
    if (result.productsResultDigest) {
      setProductsResultDigest(result.productsResultDigest);
    }
    setProductLoanDetails((prev) => ({
      ...prev,
      ...result,
    }));
  };

  const fetchProducts = async () => {
    const result = await getProducts(id);
    setProductsResultDigest(result.productsResultDigest);
    setProductsObject(result);
  };

  useEffect(() => {
    if (id && creditSearchCompleted) {
      (async () => {
        await fetchProducts();
      })();
    }
  }, [id, productsResultDigest, creditSearchCompleted]);

  useEffect(() => {
    if (id && creditSearchCompleted) {
      (async () => {
        await fetchProductLoanDetails();
      })();
    }
  }, [id, creditSearchCompleted]);

  return {
    productsObject,
    productLoanDetails,
    updateProductLoanDetails,
    fetchProducts,
    fetchProductLoanDetails,
    setProductsObject,
    setProductLoanDetails,
  };
}

export function useProductLoanDetailsAutoSave() {
  const {
    fetchAccount, setProductsResultDigest, fetchTransitionStatus,
  } = useAccountContext();
  const { productLoanDetails, setProductLoanDetails: update } = useProductsContext();

  const { setProductLoanDetails } = useProductsApi();

  useDelayCall(() => {
    (async () => {
      const { updateCount, dirtyCount } = productLoanDetails;

      const newProductLoanDetails = await setProductLoanDetails(productLoanDetails);
      await fetchTransitionStatus();

      const { productsResultDigest = null } = newProductLoanDetails;
      setProductsResultDigest(productsResultDigest);

      update((prev) => {
        if (updateCount >= prev?.updateCount && dirtyCount >= prev?.dirtyCount) {
          return {
            ...newProductLoanDetails,
            updateCount,
          };
        }
        return prev;
      });

      await fetchAccount();
    })();
  }, [productLoanDetails?.updateCount]);
}
